import { createEscalationEngine } from "./escalationEngine";
import { episodeClosed, episodeOpened } from "../episodeSignals";

const CONFIRMATION_WINDOW_MS = 2000;
const MONITOR_WINDOW_MS = 30000;
const VIOLATION_RATIO_THRESHOLD = 0.6;
const MAX_FREE_ESCALATIONS = 1;

const VIOLATION_TYPE = "FACE_PARTIAL";

const engine = createEscalationEngine({
  confirmationWindowMs: CONFIRMATION_WINDOW_MS,
  monitorWindowMs: MONITOR_WINDOW_MS,
  violationRatioThreshold: VIOLATION_RATIO_THRESHOLD,
});

let _episodeEscalations = 0;

export const faceVisibilityState = {};
export const faceVisibilityGetters = {};
export const faceVisibilityMutations = {};

export const faceVisibilityActions = {
  checkFaceVisibility(
    { dispatch },
    { box, landmarks, videoWidth, videoHeight }
  ) {
    if (!box || !videoWidth || !videoHeight) return;

    const isCutOff = false;

    // Face completeness check via landmark symmetry.
    // MediaPipe FaceMesh key indices:
    //   33  = left eye inner corner
    //   263 = right eye inner corner
    //   1   = nose tip
    //   10  = forehead center
    //   152 = chin center
    //   234 = left cheek (face contour)
    //   454 = right cheek (face contour)
    let isIncomplete = false;
    if (landmarks && landmarks.length > 0) {
      const leftEye = landmarks[33];
      const rightEye = landmarks[263];
      const nose = landmarks[1];

      const hasLeftEye =
        leftEye &&
        leftEye.x >= 0 &&
        leftEye.x <= videoWidth &&
        leftEye.y >= 0 &&
        leftEye.y <= videoHeight;
      const hasRightEye =
        rightEye &&
        rightEye.x >= 0 &&
        rightEye.x <= videoWidth &&
        rightEye.y >= 0 &&
        rightEye.y <= videoHeight;
      const hasNose =
        nose &&
        nose.x >= 0 &&
        nose.x <= videoWidth &&
        nose.y >= 0 &&
        nose.y <= videoHeight;

      // Flag as incomplete ONLY if eyes or nose are not visible in frame
      if (!hasLeftEye || !hasRightEye || !hasNose) {
        isIncomplete = true;
      }
    } else {
      isIncomplete = true;
    }

    const violating = isCutOff || isIncomplete;

    const result = engine.evaluate(Date.now(), violating, {
      xMin: Math.round(box.xMin),
      yMin: Math.round(box.yMin),
      xMax: Math.round(box.xMax),
      yMax: Math.round(box.yMax),
      isCutOff,
      isIncomplete,
    });

    switch (result.action) {
      case "confirmed":
        episodeOpened(VIOLATION_TYPE);
        dispatch("triggerViolation", {
          type: VIOLATION_TYPE,
          reason: "Face was not fully visible",
          meta: result.evidence,
        });
        break;

      case "escalate": {
        dispatch("signalEpisodeContinues", {
          type: VIOLATION_TYPE,
          reason: "Face was not fully visible",
        });
        _episodeEscalations += 1;
        if (_episodeEscalations > MAX_FREE_ESCALATIONS) {
          dispatch("triggerViolation", {
            type: VIOLATION_TYPE,
            reason: "Face not fully visible — session closed",
            isLog: true,
            meta: result.evidence,
          });
          window.dispatchEvent(new CustomEvent("face-visibility-alert"));
          dispatch("stopDetection");
        } else {
          window.dispatchEvent(new CustomEvent("face-visibility-warning"));
        }
        break;
      }

      case "resolved":
        episodeClosed(VIOLATION_TYPE);
        _episodeEscalations = 0;
        window.dispatchEvent(new CustomEvent("face-visibility-resolved"));
        break;

      default:
        break;
    }
  },

  cleanupFaceVisibility() {
    engine.reset();
    episodeClosed(VIOLATION_TYPE);
    _episodeEscalations = 0;
  },
};
